import { Divider } from "@mui/material";
import { UserPlusIcon } from "@phosphor-icons/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { Fragment, lazy, Suspense, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@/components/Button/Button";
import { Drawer } from "@/components/Drawer/Drawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import DeleteTraveller from "@/features/travellers/components/DeleteTraveller";
import {
  travellersQueryOptions,
  useDeleteTravellerMutation,
  useNewTravellerMutation,
  useUpdateTravellerMutation,
} from "@/features/travellers/hooks/useTravellers";
import { type Traveller } from "@/types/types";

import { TravelerTypeLabel } from "./TravelerTypeLabel";
import { TravellerActions } from "./TravellerActions";
import { TravellerCloseConfirmDrawer } from "./TravellerCloseConfirmDrawer";

const TravellerForm = lazy(
  () => import("@/features/travellers/components/TravellerForm"),
);

interface Props {
  edit?: string;
  delete?: string;
  new?: boolean;
}

export function TravellerListV2(props: Props) {
  const navigate = useNavigate();
  const t = useTranslations();

  const { mutateAsync: addTraveller } = useNewTravellerMutation();
  const { mutateAsync: editTraveller } = useUpdateTravellerMutation();
  const { mutateAsync: deleteTraveller } = useDeleteTravellerMutation();
  const { history } = useRouter();
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  const { data } = useSuspenseQuery(travellersQueryOptions());

  const initTravellerDeletion = (traveller: Traveller) => {
    navigate({ to: "/travelers", search: { delete: traveller.id } });
  };

  const initTravellerEdit = (traveller: Traveller) => {
    navigate({ to: "/travelers", search: { edit: traveller.id } });
  };

  const closeDrawerHandler = () => {
    history.back();
  };

  const travellerForDeletion = props.delete
    ? data?.find((t) => t.id === props.delete)
    : undefined;
  const travellerForEdit = props.edit
    ? data?.find((t) => t.id === props.edit)
    : undefined;

  return (
    <>
      <div className="section-card">
        <ul className="space-y-2">
          {data?.map((traveller, index) => (
            <Fragment key={traveller.id}>
              {index > 0 ? <Divider component="li" sx={{ my: 2 }} /> : null}

              <li className="flex justify-between">
                <div className="flex flex-1 flex-col" data-sentry-mask>
                  <span className="text-base font-medium text-text-primary">
                    {traveller.firstName} {traveller.lastName}
                  </span>
                  <span className="text-sm text-text-tertiary">
                    <TravelerTypeLabel {...traveller} /> -{" "}
                    {t("travellers.passportId")}: {traveller.passportNumber}
                  </span>
                </div>

                <div className="flex shrink-0 items-center">
                  <TravellerActions
                    traveller={traveller}
                    onDelete={initTravellerDeletion}
                    onEdit={initTravellerEdit}
                  />
                </div>
              </li>
            </Fragment>
          ))}
        </ul>
      </div>
      <div className="flex justify-center">
        <div className="my-2">
          <Button
            variant="text"
            size="small"
            onClick={() => {
              navigate({ to: "/travelers", search: { new: true } });
            }}
            color="primary"
          >
            <UserPlusIcon size={24} className="me-2 fill-text-interactive" />
            {t("travellers.addNew")}
          </Button>
        </div>
      </div>
      <Drawer
        open={Boolean(props.new || travellerForEdit)}
        disableRestoreFocus
        hasFullHeight
        heightOffset={24}
        backgroundColor="var(--color-surface-background)"
      >
        <Suspense
          fallback={
            <PageLoader>
              <AnimatedLoader icon="users" />
            </PageLoader>
          }
        >
          <TravellerForm
            onClose={(reason) => {
              if (reason === "cancelWithPendingChanges") {
                setIsCloseConfirmOpen(true);
                return;
              }

              closeDrawerHandler();
            }}
            traveller={travellerForEdit || null}
            onSubmit={(data) =>
              travellerForEdit
                ? editTraveller({ travellerId: travellerForEdit.id, data })
                : addTraveller(data)
            }
          />
        </Suspense>
      </Drawer>
      <TravellerCloseConfirmDrawer
        open={isCloseConfirmOpen}
        onClose={() => {
          setIsCloseConfirmOpen(false);
        }}
        onConfirm={() => {
          setIsCloseConfirmOpen(false);
          closeDrawerHandler();
        }}
      />
      <Drawer open={Boolean(travellerForDeletion)}>
        {travellerForDeletion ? (
          <DeleteTraveller
            traveller={travellerForDeletion}
            deleteTraveller={() => deleteTraveller(travellerForDeletion.id)}
            closeDrawer={closeDrawerHandler}
          />
        ) : null}
      </Drawer>
    </>
  );
}
